import LoadingButton from '@mui/lab/LoadingButton'
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material'
import { ChangeEvent, FC, ReactNode } from 'react'

export type InviteResponseValue = 'yes' | 'no'

type Props = {
  response: InviteResponseValue
  onResponseChange: (response: InviteResponseValue) => void
  questionLabel?: ReactNode
  acceptLabel: string
  declineLabel: string
  acceptTestId: string
  declineTestId: string
  // Only course invites collect a decline note today.
  showNoteField?: boolean
  note?: string
  onNoteChange?: (note: string) => void
  noteLabel?: string
  submitLabel: string
  submitTestId: string
  loading?: boolean
  error?: ReactNode
  onSubmit: () => void
}

/**
 * Shared accept/decline radio group + submit button used by all invite
 * accept/decline pages (public token-based and authenticated).
 */
export const InviteResponseForm: FC<Props> = ({
  response,
  onResponseChange,
  questionLabel,
  acceptLabel,
  declineLabel,
  acceptTestId,
  declineTestId,
  showNoteField,
  note,
  onNoteChange,
  noteLabel,
  submitLabel,
  submitTestId,
  loading,
  error,
  onSubmit,
}) => {
  return (
    <>
      <Box mt={3} mb={2}>
        <FormControl fullWidth>
          {questionLabel ? (
            <FormLabel id="response-q">{questionLabel}</FormLabel>
          ) : null}
          <RadioGroup
            aria-labelledby="response-q"
            name="response"
            sx={{ mt: 1 }}
            value={response}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onResponseChange(e.target.value as InviteResponseValue)
            }
          >
            <FormControlLabel
              value="yes"
              control={<Radio />}
              label={acceptLabel}
              sx={{
                border: 1,
                borderColor: 'grey.700',
                ml: 0,
                height: 50,
                borderBottom: 0,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
              }}
              data-testid={acceptTestId}
            />
            <FormControlLabel
              value="no"
              control={<Radio />}
              label={declineLabel}
              sx={{
                border: 1,
                borderColor: 'grey.700',
                ml: 0,
                height: 50,
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
              }}
              data-testid={declineTestId}
            />
          </RadioGroup>
        </FormControl>

        {showNoteField && response === 'no' && (
          <Box width="80%" mt={2}>
            <TextField
              id="note"
              variant="filled"
              label={noteLabel}
              placeholder={noteLabel}
              fullWidth
              value={note}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onNoteChange?.(e.target.value)
              }
            />
          </Box>
        )}
      </Box>

      <LoadingButton
        loading={loading}
        type="button"
        variant="contained"
        color="primary"
        data-testid={submitTestId}
        size="large"
        onClick={onSubmit}
      >
        {submitLabel}
      </LoadingButton>

      {error ? (
        <FormHelperText sx={{ mt: 2 }} error>
          {error}
        </FormHelperText>
      ) : null}
    </>
  )
}
